import {cn} from '@tempmee/core'
import {type ButtonHTMLAttributes, type ReactNode, forwardRef} from 'react'
import {CircularProgress} from '../circular-progress'
import {ButtonVariantsFactory} from './Variants'

export type ButtonVariant = 'contained' | 'outlined' | 'text'
export type ButtonColor = 'primary' | 'secondary' | 'error' | 'cta' | 'inverse'

/** @deprecated Legacy single-prop variants. Use `variant` + `color` instead. */
export type LegacyButtonVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'warning'
  | 'light'
  | 'white'
  | 'ghost'

const LEGACY_MAP: Record<LegacyButtonVariant, {variant: ButtonVariant; color: ButtonColor}> = {
  primary: {variant: 'contained', color: 'primary'},
  secondary: {variant: 'contained', color: 'secondary'},
  tertiary: {variant: 'contained', color: 'secondary'},
  warning: {variant: 'contained', color: 'error'},
  light: {variant: 'contained', color: 'inverse'},
  white: {variant: 'contained', color: 'inverse'},
  ghost: {variant: 'text', color: 'secondary'},
}

function isLegacyVariant(v: string | undefined): v is LegacyButtonVariant {
  return v != null && v in LEGACY_MAP
}

export type ButtonSize = 'small' | 'medium' | 'large'

/** @deprecated Legacy size names. Use `small`/`medium`/`large`. */
export type LegacyButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'inline'

const LEGACY_SIZE_MAP: Record<LegacyButtonSize, ButtonSize> = {
  xs: 'small',
  sm: 'small',
  md: 'medium',
  lg: 'large',
  inline: 'small',
}

function resolveSize(size: ButtonSize | LegacyButtonSize | undefined): ButtonSize | undefined {
  if (size == null) return undefined
  return size in LEGACY_SIZE_MAP ? LEGACY_SIZE_MAP[size as LegacyButtonSize] : (size as ButtonSize)
}

/** Colors available for outlined/text — `cta` is contained-only (per Figma). */
export type ButtonLineColor = Exclude<ButtonColor, 'cta'>

interface ButtonBaseProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Button size. Legacy values (`xs`/`sm`/`md`/`lg`/`inline`) are accepted and
   * mapped to `small`/`medium`/`large` (deprecated). @default 'medium'
   */
  size?: ButtonSize | LegacyButtonSize
  /** Icon rendered before the label. Replaced by a spinner while loading. */
  startIcon?: ReactNode
  /** Icon rendered after the label. */
  endIcon?: ReactNode
  /** Disables the button and shows a spinner in the leading slot. */
  loading?: boolean
  /** @deprecated Use `startIcon`. */
  LeadingIcon?: ReactNode
  /** @deprecated Use `endIcon`. */
  TrailingIcon?: ReactNode
  /** @deprecated Use `loading`. */
  isLoading?: boolean
}

/**
 * Two-axis Button. **`cta` is contained-only** (per Figma) — the type forbids
 * `color="cta"` on `outlined`/`text`.
 */
export type ButtonProps = ButtonBaseProps &
  (
    | {
        /**
         * Visual style. New API: `contained` | `outlined` | `text`. Legacy
         * single-prop values (`primary`, `ghost`, …) are accepted (deprecated).
         * @default 'contained'
         */
        variant?: 'contained' | LegacyButtonVariant
        /** Semantic color. Ignored when a legacy `variant` value is used. @default 'primary' */
        color?: ButtonColor
      }
    | {
        /** Visual style. @default 'contained' */
        variant: 'outlined' | 'text'
        /** Semantic color (no `cta` — that's contained-only). @default 'primary' */
        color?: ButtonLineColor
      }
  )

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      color,
      size,
      startIcon,
      endIcon,
      loading,
      LeadingIcon,
      TrailingIcon,
      isLoading,
      disabled,
      children,
      ...props
    },
    ref,
  ) => {
    // Normalize legacy single-prop variants → {variant, color}.
    const legacy = isLegacyVariant(variant)
    const resolvedVariant: ButtonVariant = legacy
      ? LEGACY_MAP[variant].variant
      : (variant ?? 'contained')
    const baseColor: ButtonColor = legacy ? LEGACY_MAP[variant].color : (color ?? 'primary')
    // `cta` is contained-only. The prop type already forbids cta on outlined/text
    // for TS callers; this guard degrades the invalid combo to `primary` for any
    // non-typed caller (plain JS, Storybook controls) so it never renders unstyled.
    const resolvedColor: ButtonColor =
      baseColor === 'cta' && resolvedVariant !== 'contained' ? 'primary' : baseColor

    const leading = startIcon ?? LeadingIcon
    const trailing = endIcon ?? TrailingIcon
    const busy = loading ?? isLoading

    // The ring inherits the button's text color (white on contained, colored otherwise).
    const spinner = <CircularProgress size={16} color="inherit" />

    return (
      <button
        className={cn(
          ButtonVariantsFactory({
            variant: resolvedVariant,
            color: resolvedColor,
            size: resolveSize(size),
          }),
          className,
        )}
        ref={ref}
        disabled={busy || disabled}
        aria-busy={busy}
        {...props}
      >
        {busy ? spinner : (leading ?? null)}
        <span className="truncate">{children}</span>
        {!busy && (trailing ?? null)}
      </button>
    )
  },
)

Button.displayName = 'Button'
